import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Shop } from '@shared/components/ui/shop';
import { Incident, IncidentStatus } from '../../models/Incident';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faHistory, faPlay } from '@fortawesome/pro-light-svg-icons';
import { Courier } from '../../../../../Mappings/List/hooks/useMappings';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';

interface CardProps {
  incident: Incident;
  onOpenActions?: (incident: Incident) => void;
  onOpenHistory?: (incident: Incident) => void;
  selectedColumns?: string[];
  couriersMap?: Map<number, Courier>;
}

// Colores alineados a la tabla desktop (useIncidentsAgGridColumns)
const statusColorMap: Record<IncidentStatus, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-card-border light:bg-badge-bg-status-1', text: 'text-badge-status-1', label: 'Pendiente' },
  in_progress: { bg: 'bg-card-border light:bg-bg-warning', text: 'text-warning', label: 'En progreso' },
  resolved: { bg: 'bg-bg-success', text: 'text-success', label: 'Resuelta' },
  canceled: { bg: 'bg-bg-error', text: 'text-error', label: 'Cancelada' },
};

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '-');

export const Card = ({ incident, onOpenActions, onOpenHistory, selectedColumns, couriersMap }: CardProps) => {
  const { t } = useTranslation('incidentsModule');
  const { getTeamType } = usePermissions();
  const is3pl = getTeamType() === 'tenant_3pl';
  const isVisible = (columnId: string) => !selectedColumns || selectedColumns.includes(columnId);

  const statusMeta = statusColorMap[incident.status] || statusColorMap.pending;
  const isPending = incident.status === 'pending';

  return (
    <div className="py-6 border-b border-card-border">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <Link
            to={`/orders/view/${incident.order_id}`}
            className="text-sm font-semibold text-title hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {incident.order_ref?.trim() || t('table.order_link_fallback')}
          </Link>
          {(isVisible('order_ref') ||
            isVisible('shop_name') ||
            (is3pl && isVisible('shop_team_name')) ||
            isVisible('tracking_number')) && (
            <div className="mt-2 space-y-1 text-xs text-complementary-text">
              {isVisible('order_ref') && (
                <div>
                  <span className="text-complementary-text">{t('table.order_ref')}: </span>
                  <span className="font-medium text-text">{incident.order_ref?.trim() || '-'}</span>
                </div>
              )}
              {isVisible('shop_name') && (
                <div>
                  <span className="text-complementary-text">{t('table.shop_name')}: </span>
                  <span className="font-medium text-text">{incident.shop_name?.trim() || '-'}</span>
                </div>
              )}
              {is3pl && isVisible('shop_team_name') && (
                <div>
                  <span className="text-complementary-text">{t('table.shop_team_name')}: </span>
                  <span className="font-medium text-text">{incident.shop_team_name?.trim() || '-'}</span>
                </div>
              )}
              {isVisible('tracking_number') && (
                <div>
                  <span className="text-complementary-text">{t('table.tracking_number')}: </span>
                  <span className="font-mono font-medium text-text">{incident.tracking_number?.trim() || '-'}</span>
                </div>
              )}
            </div>
          )}
          {isVisible('status') && (
            <div className="mt-1 flex items-center justify-between gap-2">
              <Badge className={`text-xs ${statusMeta.bg} ${statusMeta.text}`}>{statusMeta.label}</Badge>
              {isVisible('courier') && (
                <div className="ml-auto">
                  {incident.courier?.name || couriersMap?.get(Number(incident.courier_id))?.name ? (
                    <Shop
                      name={incident.courier?.name || couriersMap?.get(Number(incident.courier_id))?.name}
                      src={
                        incident.courier?.image_hash ||
                        incident.courier?.logo ||
                        couriersMap?.get(Number(incident.courier_id))?.image_hash ||
                        undefined
                      }
                    />
                  ) : (
                    <span className="text-sm text-complementary-text">-</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          {isPending ? (
            <Button
              size="icon"
              variant="primary"
              onClick={() => onOpenActions?.(incident)}
              title="Acciones"
              className="w-9 h-9"
            >
              <FontAwesomeIcon icon={faPlay} className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onOpenHistory?.(incident)}
              title="Historial"
              className="w-9 h-9"
            >
              <FontAwesomeIcon icon={faHistory} className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Tipo externo */}
      {isVisible('external_type') && (
        <div className="mt-3 text-sm space-y-1">
          <div className="text-xs text-complementary-text">{t('table.external_type')}</div>
          {incident.external_label?.trim() ? (
            <div className="font-medium">{incident.external_label.trim()}</div>
          ) : (
            <div className="text-xs text-error">{t('table.unmapped')}</div>
          )}
        </div>
      )}

      {/* Fechas */}
      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        {isVisible('report_date') && (
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-complementary-text" />
            <div>
              <div className="text-xs text-complementary-text">Reportada</div>
              <div className="font-medium">{formatDate(incident.report_date)}</div>
            </div>
          </div>
        )}
        {isVisible('resolution_date') && (
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-complementary-text" />
            <div>
              <div className="text-xs text-complementary-text">Resuelta</div>
              <div className="font-medium">{formatDate(incident.resolution_date)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
